import type { SportPhysicsParams } from './sport-definitions/sport-definition.interface';

// ─── Abstraction ───────────────────────────────────────────────────────────────

/**
 * Physics engine abstraction.
 *
 * Phase 1: SimplifiedPhysicsEngine — pure analytical math, no external dependencies.
 * Phase 5+: CannonPhysicsEngine can implement this same interface for full rigid-body
 *           simulation. Swap = change one constructor call in the renderer.
 */
export interface IPhysicsEngine {
  /**
   * Build a set of arc sample points for ball animation.
   * Returns an array of [x, y, z] tuples along a parabolic arc from start to end.
   *
   * @param start  Ball start position in scene units
   * @param end    Ball end position in scene units
   * @param params Sport physics params (used for arcHeightFactor)
   * @param steps  Number of sample points (default 30)
   */
  buildArcPoints(
    start: { x: number; y: number; z: number },
    end: { x: number; y: number; z: number },
    params: SportPhysicsParams,
    steps?: number
  ): Array<[number, number, number]>;
}

// ─── Implementation ────────────────────────────────────────────────────────────

/**
 * Phase 1 physics engine: analytical parabolic arc.
 *
 * The shot evaluation itself lives in each ISportDefinition.evaluateShot() —
 * this engine only provides the animation arc points for the scene component.
 * This keeps sport-specific physics (whiff threshold, distance bands, shank drift)
 * in the right place while giving the scene a shared curve-building utility.
 */
export class SimplifiedPhysicsEngine implements IPhysicsEngine {
  buildArcPoints(
    start: { x: number; y: number; z: number },
    end: { x: number; y: number; z: number },
    params: SportPhysicsParams,
    steps = 30
  ): Array<[number, number, number]> {
    const { arcHeightFactor } = params;

    // Horizontal distance for peak height calculation
    const dx = end.x - start.x;
    const dz = end.z - start.z;
    const horizontalDist = Math.sqrt(dx * dx + dz * dz);
    const peakHeight = horizontalDist * arcHeightFactor;

    const points: Array<[number, number, number]> = [];

    for (let i = 0; i <= steps; i++) {
      const t = i / steps;

      // Linear interpolation for x and z
      const x = start.x + dx * t;
      const z = start.z + dz * t;

      // Parabolic arc for y: peaks at t=0.5
      // y(t) = 4 * peakHeight * t * (1 - t)  +  lerp(start.y, end.y, t)
      const baseY = start.y + (end.y - start.y) * t;
      const arcY = 4 * peakHeight * t * (1 - t);
      const y = baseY + arcY;

      points.push([x, y, z]);
    }

    return points;
  }
}
